export interface SpecifyFindingLike {
  code: string;
  message: string;
}

export const CURATED_HUMAN_FINDING_LABELS: Readonly<Record<string, string>> = {
  "agent.tool_loop_non_convergence":
    "The bounded repair loop ended with unresolved checks",
  "agent.provider_invalid_turn":
    "The provider failed before a schema-valid proposal could be verified",
  "agent.repeated_rejected_controller_allocation":
    "The agent repeated a previously rejected controller allocation",
  "agent.tag_annotation_unmatched": "Some tag annotations target missing output rows",
  "agent.unexplained_anchor_change":
    "Controller changes need a cited verifier or source explanation",
  "agent.unexplained_infrastructure_anchor_change":
    "Infrastructure changes need a cited verifier or source explanation",
  "controller.explicit_decision_disagreement":
    "Controller proposal disagrees with the explicit decision",
  "controller.point_capacity_exceeded": "Controller point capacity was exceeded",
  "controller.tie_policy_deviation": "Controller tie-policy guidance needs review",
  "controller.zone_capacity_exceeded": "Controller zone capacity was exceeded",
  "coverage.unknown_zone_referenced":
    "A proposal references take-off zones that were not found",
  "coverage.zone_uncovered": "One or more take-off zones have no proposed coverage",
  "infrastructure.io_com_relay_shortfall": "I/O COM relay output capacity is short",
  "infrastructure.io_com_relay_surface_mismatch":
    "Relay capacity must be shown as I/O COM module rows",
  "infrastructure.io_com_sizing_contradicts_evidence":
    "I/O COM module counts disagree with the sizing evidence",
  "infrastructure.io_com_sizing_evidence_refs_missing":
    "I/O COM module rows need the sizing evidence attached",
  "infrastructure.netmaster_shortfall": "NetMaster provision is short",
  "infrastructure.protocol_shortfall":
    "Required infrastructure protocol exposure could not be confirmed",
  "infrastructure.srm_relay_quantity_contradicts_evidence":
    "SRM relay module quantity disagrees with the sizing evidence",
  "infrastructure.srm_relay_shortfall": "SRM relay output capacity is short",
  "infrastructure.srm_sizing_evidence_missing":
    "SRM relay sizing needs supporting evidence",
  "infrastructure.srm_sizing_evidence_refs_missing":
    "SRM relay rows need the sizing evidence attached",
  "infrastructure.tie_policy_deviation":
    "Infrastructure tie-policy guidance needs review",
  "metering.bms_exposure_missing": "BMS metering exposure could not be confirmed",
  "metering.protocol_mismatch": "BACnet metering protocol could not be matched",
  "metering.required_rows_missing": "Required metering rows were not supplied",
  "metering.resident_display_missing":
    "Resident metering display could not be confirmed",
  "metering.topology_mismatch": "Metering topology could not be matched",
  "overflow.over_overflow_mz_capacity":
    "Overflow controller capacity appears higher than the remaining load needs",
  "policy.basis_evidence_unresolved":
    "Evidence-backed scope or install basis needs a matching source",
  "policy.default_unlabelled":
    "Defaulted decisions need WHY/WHERE evidence or an explicit default label",
  "policy.install_included_basis_contradiction":
    "Install-included scope contradicts its stated basis",
  "policy.material_basis_missing":
    "Infrastructure scope and install basis disclosures are missing",
  "policy.material_presence_contradicts_evidence":
    "Required infrastructure presence or quantity disagrees with evidence",
  "policy.material_value_contradicts_evidence":
    "Infrastructure scope or install values disagree with evidence",
  "policy.material_value_evidence_conflict":
    "Conflicting same-authority evidence needs a user decision",
  "policy.material_value_evidence_overridden":
    "A lower-authority default was overridden by stronger evidence",
  "provenance.source_or_evidence_missing":
    "Proposal entries are missing source or evidence references",
  "spare.mz_with_spare_second_zone_slots_and_unassociated_zones":
    "Overflow controls may be avoidable while FCU spare zone slots remain",
  "coverage.zone_double_covered": "A zone may be covered by more than one controller"
};

export const curatedHumanFindingLabel = (
  finding: SpecifyFindingLike
): string | undefined => CURATED_HUMAN_FINDING_LABELS[finding.code];

export const primaryHumanFindingLabel = (finding: SpecifyFindingLike): string =>
  curatedHumanFindingLabel(finding) ?? "Additional technical finding needs review";

export const humanFindingLabel = (finding: SpecifyFindingLike): string =>
  curatedHumanFindingLabel(finding) ??
  `Technical finding: ${finding.code.trim() || "unknown code"}`;
